// The reflection-capture blend: up to MAX_BLEND baked equirects combined by
// weight into one texture, which `PMREMGenerator` then prefilters into the
// specular envMap.
//
// TSL ONLY, and that is a correctness requirement rather than a preference. The
// combine is a fullscreen pass, and it used to be a raw-GLSL `ShaderMaterial`;
// under a node renderer three does not merely fail to draw one, it throws inside
// `_renderObjectDirect` mid-frame, leaves the command encoder unfinished, and
// from then on every render for every scene silently draws nothing.
// `ReflectionCaptureVolume` mounts UNCONDITIONALLY, so that is the whole app.
//
// IT WRITES LINEAR RADIANCE, into a half-float target with no colour space. The
// only reader is `PMREMGenerator`, which wants exactly that, and the pairing is
// GROUND-TRUTHED rather than argued: with one capture at weight 1 the blend
// reproduces the source PNG's own irradiance exactly — 84.0 on a grey sphere
// beside the head, against 84.0 for a PMREM taken straight from the PNG. The
// 8-bit sRGB target this replaced reads 100.4, about 16 levels too bright,
// because tagging an 8-bit target `SRGBColorSpace` gets it an `SRGB8_ALPHA8`
// format and WebGL2 applies linear->sRGB on write to such a target
// UNCONDITIONALLY (ES3 has no `GL_FRAMEBUFFER_SRGB` toggle) — so the shader's
// own encode survived the sampler's decode. If someone wants that brighter look
// the honest lever is exposure or `envMapIntensity`, not a transfer curve that
// misreads the source data.
//
// A reason recorded here previously — that the sRGB target made the two BACKENDS
// disagree by 24-32 levels — was a misattribution. That split was the
// environment map being upside down on the node renderer (see
// `flipsRenderTargetV` below); with the flip fixed the two backends agree to 0.3
// levels on either target. The target is linear because it is correct, not
// because it settled an argument between backends.
//
// The INPUT conversion stays hand-written rather than delegated to three's
// colour-space nodes: the capture PNGs are sRGB and must be linearised exactly
// once, and a second conversion applied by a sampler is a change nobody would
// see except as reflections that are subtly wrong.

/** Captures blended at once (a grid cell has <= 4 in range). */
export const MAX_BLEND = 4

/**
 * THE VERTICAL AXIS OF A RENDER TARGET IS NOT THE SAME ON THE TWO GRAPHICS
 * APIS, and this pass is exactly the shape that notices.
 *
 * It draws a CLIP-SPACE QUAD and samples by the geometry's `uv`, so it relies on
 * a correspondence between where a fragment LANDS (NDC y) and which texel of the
 * result that is (v). WebGL puts the framebuffer origin at the bottom-left AND
 * texel row 0 at the bottom, so NDC y=+1 is v=1. WebGPU puts the framebuffer
 * origin at the TOP-left while texel row 0 is also the top, so NDC y=+1 is v=0.
 * The two are exactly inverted, and nothing in three reconciles them for a quad
 * drawn this way.
 *
 * So on the WebGPU backend the blended equirect came out UPSIDE DOWN, and since
 * the only reader is `PMREMGenerator` the whole environment was vertically
 * mirrored: the studio's bright floor lit the avatar from above and its dark
 * ceiling from below. MEASURED with a neutral grey sphere beside the head, top
 * third minus bottom third of the diffuse ball:
 *
 *   baked light probe (unaffected, both arms)   -45
 *   IBL, WebGL                                  +27
 *   IBL + probe, WebGL                           -1   <- the two nearly cancel
 *   IBL + probe, WebGPU, before                 -56   <- they compound instead
 *   IBL + probe, WebGPU, with this flip          +4
 *
 * That is the whole of "the lighting is flatter and reads wrong": the probe
 * lights this room from below (a white cyc floor, black acoustic walls), the IBL
 * is what puts light back on top of everything, and inverted it took the top
 * light away and doubled the uplight instead.
 *
 * IT IS THE RENDERER, NOT THE GRAPHICS API, and that was worth measuring rather
 * than reasoning out. The obvious reading is that this is a WebGPU-versus-WebGL
 * fact and should be keyed to `backend.isWebGPUBackend`; it is not.
 * `WebGPURenderer` presents ONE coordinate convention to the code it runs, and
 * its own WebGL2 fallback backend flips underneath to honour it — so forcing
 * that fallback measured EXACTLY the WebGPU arm's numbers (-56.4 on the diffuse
 * ball, to the decimal). Keyed to the backend, the fix would have landed on the
 * default path and left the fallback mirrored, which no developer box exercises
 * by default. So the test is the renderer CLASS, and it is read off the live
 * renderer rather than inferred from a flag, as `canvasRenderer.js` requires
 * generally.
 *
 * WHY A TWO-POINT FACE MEASUREMENT MISSED IT, since this flip was tried once
 * before and rejected: the patches were a forehead and a cheek, both FRONT-
 * facing, and a vertical inversion of the environment barely moves a surface
 * whose normal points at the camera. The instrument could not see the axis the
 * bug was on. A sphere shows all normals at once.
 */
export const flipsRenderTargetV = (renderer) => renderer?.isWebGPURenderer === true

/**
 * The render-target options this pass writes into. `THREE` is passed in rather
 * than imported so the caller's instance is the one used — and because the two
 * halves of the decision, the FORMAT and what the shader writes, are one choice:
 * a linear target fed sRGB values is everything too bright, an sRGB target fed
 * linear ones is everything too dark, and both render without an error anywhere.
 * The shader above writes linear radiance, so this is half-float and carries no
 * colour space.
 */
export function blendTargetOptions(THREE) {
  return {
    format: THREE.RGBAFormat,
    type: THREE.HalfFloatType,
    colorSpace: THREE.NoColorSpace,
    depthBuffer: false,
    stencilBuffer: false,
    minFilter: THREE.LinearFilter,
    magFilter: THREE.LinearFilter,
  }
}

/**
 * The blend material, plus the ONE way to feed it. The setter exists so no
 * caller reaches into the node graph to retarget a sampler.
 *
 * @param {object} [o]
 * @param {object} [o.renderer] the live renderer, so the vertical convention is
 *   read rather than guessed. Omitted only where there is none (unit tests).
 * @param {boolean} [o.flipV] override that correction, for a harness rendering
 *   both conventions in one process.
 * @returns {Promise<{material: object, setInputs: (textures: any[], weights: number[]) => void, flipV: boolean, dispose: () => void}>}
 */
export async function buildReflectionBlendMaterial({
  renderer = null,
  flipV = flipsRenderTargetV(renderer),
} = {}) {
  // Through the app's OWN module: `three/webgpu` and `three/tsl` are named in
  // exactly one place so Vite's prebundle list and the imports stay in step.
  const { WGPU, TSL } = await import('../ogs/render/three/threeSplatDeps.js')
  const { Fn, texture, uniform, uv, vec2, vec3, vec4, float, mix, pow, step } = TSL

  // THE FUNCTION FORM, NOT THE METHOD FORM, and it is not style. TSL method
  // chaining puts the RECEIVER FIRST — `c.step(0.04045)` is `step(c, 0.04045)`,
  // the colour read as the edge. It compiles, it runs, and it returns the wrong
  // branch of every mix with no error anywhere. (The see-through fade shipped
  // inert for exactly this reason.)
  const srgbToLinear = Fn(([c]) => mix(
    c.div(12.92),
    pow(c.add(0.055).div(1.055), vec3(2.4)),
    step(float(0.04045), c),
  ))
  // See `flipsRenderTargetV` above: on the WebGPU backend the fragment that
  // lands at the top of the target is the one the sampler will later read at
  // v=0, so the source has to be read from the other end to put the equirect
  // back the right way up.
  const uvNode = flipV ? vec2(uv().x, uv().y.oneMinus()) : uv()
  // One sampler node and one weight node per slot, kept so the per-frame setter
  // can retarget them without rebuilding the graph — the pass re-runs only when
  // a weight moves past WEIGHT_EPS, but rebuilding a material there would
  // recompile a shader mid-frame.
  //
  // Weights are a normalised sum of UE's DistanceAlpha (see
  // `ReflectionCaptureVolume`'s useFrame): the same fade function and
  // influence-radius semantics as UE, but a normalised sum rather than UE's
  // order-dependent "under" operator — for ONE lit point on a regular auto-grid
  // (no Lumen/SSR fallback, no artist-nested captures) the under-operator's flat
  // core collapses to nearest-wins and re-pops, and leftover alpha would leak to
  // an absent skylight. Unused slots carry w=0 and contribute nothing.
  const texNodes = [], weightNodes = []
  let acc = null
  for (let k = 0; k < MAX_BLEND; k++) {
    const t = texture(null, uvNode)
    const w = uniform(0)
    texNodes.push(t); weightNodes.push(w)
    const term = srgbToLinear(t.rgb).mul(w)
    acc = acc === null ? term : acc.add(term)
  }

  const material = new WGPU.NodeMaterial()
  material.depthTest = false
  material.depthWrite = false
  // `fragmentNode` / `vertexNode` short-circuit the whole lighting and
  // output-colour-space machinery, which is what keeps this pass a plain
  // weighted sum rather than the sum plus whatever three would otherwise apply.
  // The geometry is a clip-space quad — a projection here would render nothing.
  material.vertexNode = vec4(TSL.positionGeometry, 1)
  material.fragmentNode = vec4(acc, 1)

  return {
    material,
    flipV,
    setInputs(textures, weights) {
      for (let k = 0; k < MAX_BLEND; k++) {
        texNodes[k].value = textures[k]
        weightNodes[k].value = weights[k]
      }
    },
    dispose() { material.dispose() },
  }
}
